import React, { useState, useMemo } from "react";
import { makeStyles } from "@mui/styles";
import { useNavigate } from "react-router-dom";
import { Grid, Typography, Card, Button, TextField, Stack } from "@mui/material";
import colors from "../../../../colors";
import SingleLatestTransaction from "../SingleLatestTransaction";
import { texts } from "../../../../general/texts";
import ButtonGroup from "../../../../general/ButtonGroup";
import ReactList from "react-list";
import LazyLoading from "react-list-lazy-load";
import DateRange from "./DateRange";
import EmptyTransactionHistory from "./EmptyTransactionHistory";
import { filter } from "./TransactionFilter";
import TransactionDetailsDialog from "../TransactionDetails";
import EmailIcon from "@mui/icons-material/EmailOutlined";
import Fab from "@mui/material/Fab";
import GetAppIcon from "@mui/icons-material/GetApp";
import { sendGeneralRequest } from "../../../../../features/generalRequest";
import moment from "moment";
import { selectGlobalDataState } from "../../../../../features/global/globalDataSlice";
import { useDispatch, useSelector } from "react-redux";
import { selectUserInfo, setIsTransactionDataFiltered } from "../../../../../features/userInfo/userInfoSlice";
import LinearProgress from "@mui/material/LinearProgress";
import { setGeneralMessage } from "../../../../../features/global/globalDataSlice";
import BarChartIcon from '@mui/icons-material/BarChart';
import ChartModal from "./ChartModal";

export const TransactionHistory = () => {
  const [selectedTransaction, setSelectedTransaction] = useState(null);
  const dispatch = useDispatch();
  const globalDataSliceState = useSelector(selectGlobalDataState);
  const userInfo = useSelector(selectUserInfo);
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(false);
  const [isChartModalOpen, setIsChartModalOpen] = useState(false);

  const historyToUse = !userInfo.isTransactionDataFiltered ? userInfo.defaultTransactionHistory : userInfo.transactionHistory;

  const handleTransactionClick = (transactionData) => {
    setSelectedTransaction(transactionData);
  };

  const filterWithDates = () => {
    dispatch(
      sendGeneralRequest({
        request: {
          token: globalDataSliceState.token,
          fromDate: formatDate(dateRange[0]),
          toDate: formatDate(dateRange[1]),
        },
        baseUrlKey: "services",
        endpoint: "GetStatement",
      })
    );
   dispatch(setIsTransactionDataFiltered(true))
  };

  const manageFileStatement = async (
    fileType,
    fromDate = moment().subtract(1, "months").format("DD/MM/YYYY"),
    toDate = moment().format("DD/MM/YYYY")
  ) => {
    try {
      setIsLoading(true);
      const response = await dispatch(
        sendGeneralRequest({
          request: {
            token: globalDataSliceState.token,
            fromDate: fromDate,
            toDate: toDate,
            fileType: fileType,
          },
          baseUrlKey: "services",
          endpoint: "StatementFileManagment",
        })
      );
      if (fileType === "PDF") {
        navigate("/pdf", {
          state: { stream: response.payload.response.stream },
        });
      }
    } catch (error) {
      console.error(error);
    } finally {
      setIsLoading(false);
      if (fileType === "EMAIL") {
        dispatch(
          setGeneralMessage({
            type: "success",
            message: texts["email-sent-success"],
          })
        );
      }
    }
  };

  const formatDate = (date) => {
    if (!(date instanceof Date && !isNaN(date))) {
      return ""; 
    }

    let year = new Intl.DateTimeFormat("en", { year: "numeric" }).format(date);
    let month = new Intl.DateTimeFormat("en", { month: "2-digit" }).format(date);
    let day = new Intl.DateTimeFormat("en", { day: "2-digit" }).format(date);
    var formattedDate = `${day}/${month}/${year}`;
  
    return formattedDate;
  };
  

  const handleClose = () => {
    setSelectedTransaction(null);
  };

  const handleOpenChartModal = () => {
    setIsChartModalOpen(true);
  };

  const handleCloseChartModal = () => {
    setIsChartModalOpen(false);
  };

  const useStyles = makeStyles({
    scrollBar: {
      "&::-webkit-scrollbar": {
        width: "10px",
        height: "50%",
      },
      "&::-webkit-scrollbar-track": {
        backgroundColor: "#FFFFFF",
        borderRadius: "10px",
      },
      "&::-webkit-scrollbar-thumb": {
        background: "linear-gradient(to bottom, #EF7A30, #F0A13D)",
        borderRadius: "10px",
        maxHeight: "50%",
      },
      "&::-webkit-scrollbar-thumb:hover": {
        backgroundColor: "linear-gradient(to bottom, #EF7A30, #F0A13D)",
      },
    },
    scrollBarStack: {
      "&::-webkit-scrollbar": {
        width: "50%",
        height: "5px",
      },
      "&::-webkit-scrollbar-track": {
        backgroundColor: "#FFFFFF",
        borderRadius: "10px",
      },
      "&::-webkit-scrollbar-thumb": {
        background: colors.mediumGray,
        borderRadius: "10px",
        maxWidth: "50%",
      },
      "&::-webkit-scrollbar-thumb:hover": {
        backgroundColor: colors.mediumGray,
      },
    },
  });

  const classes = useStyles();

  const [dateRange, setDateRange] = React.useState([null, null]);
  const [searchBarValue, setSearchBarValue] = React.useState("");
  const [tag, setTag] = React.useState("");
  const [shouldResetButtonGroup, setShouldResetButtonGroup] =
    React.useState(false);

  const determineName = () => {
    if (tag === "" && searchBarValue === "") {
      return "";
    } else if (tag !== "" && searchBarValue === "") {
      return tag;
    } else {
      return searchBarValue;
    }
  };

  const transactions = React.useMemo(
    () => filter(historyToUse.transactions, determineName()),
    [searchBarValue, tag, historyToUse]
  );

  const clearSearch = () => {
    setDateRange([null, null]);
    setTag("");
    setSearchBarValue("");
    setShouldResetButtonGroup(!shouldResetButtonGroup);
  };

  const selectedButtonStyle = {
    backgroundColor: colors.lightOrange,
    color: colors.orange,
    width: "190px",
    height: "40px",
    fontFamily: "GerberaBold",
    textTransform: "none",
    fontSize: "1rem",
    border: "2px solid" + colors.orange,
    borderRadius: "50px",
    "&:hover": {
      backgroundColor: colors.lightOrange,
    },
  };

  const defaultButtonStyle = {
    backgroundColor: colors.lightBlue,
    minWidth: "190px",
    height: "40px",
    borderRadius: "50px",
    textTransform: "none",
    fontFamily: "GerberaLight",
    fontSize: "1rem",
    color: colors.darkBlue,
    "&:hover": {
      backgroundColor: colors.lightOrange,
    },
  };

  return (
    <Grid container item md={12} xs={12} justifyContent={"center"} pt={"2%"}>
      <Card
        sx={{
          borderRadius: "20px",
          boxShadow: "0 0 15px 5px #E5E7EA",
          padding: "2%",
          background: colors.white,
          width: "54%",
          minHeight: "65vh",
          "@media (max-width: 899px)": { width: "80%" },
        }}
      >
        <Grid
          container
          item
          md={12}
          xs={12}
          columnSpacing={0.5}
          justifyContent={"space-between"}
          rowSpacing={2}
          mb={1.5}
        >
          <Grid
            item
            md={2}
            pb={2}
            xs={2}
            justifyContent={"center"}
            alignItems={"center"}
          >
            <Typography
              sx={{
                color: colors.darkBlue,
                fontWeight: "bold",
                fontSize: "2rem",
              }}
            >
              {texts["title-history"]}
            </Typography>
          </Grid>
          <Grid
            item
            md={3}
            xs={12}
            justifyContent={"center"}
            alignItems={"center"}
            mt={0.5}
          >
            <TextField
              fullWidth
              size={"small"}
              value={searchBarValue}
              color={"warning"}
              variant={"outlined"}
              label={"Kërko"}
              onChange={(e) => {
                setSearchBarValue(e.target.value);
                setShouldResetButtonGroup(!shouldResetButtonGroup);
              }}
              sx={{
                background: colors.cyan,
                borderRadius: "10px",
                [`& fieldset`]: {
                  borderColor: colors.cyan,
                  borderRadius: "10px",
                },
              }}
            ></TextField>
          </Grid>
          <DateRange
            dateRange={dateRange}
            onChange={(newDateRange) => setDateRange(newDateRange)}
          />
          <Button
            style={{ marginTop: "2%" }}
            onClick={filterWithDates}
            disabled={dateRange[0] === null && dateRange[1] === null}
            sx={{
              background: colors.gradientOrange,
              minWidth: "190px",
              height: "40px",
              borderRadius: "10px",
              textTransform: "none",
              fontFamily: "GerberaMedium",
              fontSize: "1rem",
              color: colors.white,
              "&:hover": {
                backgroundColor: colors.lightOrange,
              },
            }}
          >
            {" "}
            {texts["prompt-search-with-date"]}
          </Button>
        </Grid>
        <Grid
          container
          direction="row"
          justifyContent="space-between"
          alignItems="center"
          marginBottom={2}
          spacing={2}
        >
          <Grid item md={2.5} xs={6}>
            <Typography
              sx={{
                color: colors.darkBlue,
                fontWeight: "bold",
                fontSize: "0.75rem",
              }}
            >
              {texts["openingBalance"]}: &nbsp;
              {historyToUse &&
              historyToUse.statement &&
              typeof historyToUse.statement.openingBalance !==
                "undefined" &&
                historyToUse.statement.openingBalance !== ""
                ? historyToUse.statement.openingBalance
                : "0.0"}
              &nbsp; {texts["currency"]}
            </Typography>
          </Grid>
          <Grid item md={3} xs={6}>
            <Typography
              sx={{
                color: colors.darkBlue,
                fontWeight: "bold",
                fontSize: "0.75rem",
              }}
            >
              {texts["closingBalance"]}: &nbsp;
              {historyToUse &&
              historyToUse.statement &&
              typeof historyToUse.statement.closingBalance !==
                "undefined" &&
                historyToUse.statement.closingBalance !== ""
                ? historyToUse.statement.closingBalance
                : "0.0"}
              &nbsp; {texts["currency"]}
            </Typography>
          </Grid>
        </Grid>
        {historyToUse.tags.length !== 0 && (
          <Stack
            flexDirection={"row"}
            maxWidth={"1080px"}
            overflow={"auto"}
            p={1}
            columnGap={1}
            className={classes.scrollBarStack}
          >
            <Button onClick={clearSearch} sx={defaultButtonStyle}>
              {texts["all"]}
            </Button>
            <ButtonGroup
              key={shouldResetButtonGroup}
              defaultButtonStyle={defaultButtonStyle}
              selectedButtonStyle={selectedButtonStyle}
              state={historyToUse.tags.map((tag) => ({
                display: tag,
                value: tag.toLowerCase(),
              }))}
              sendValue={(value) => {
                setTag(value);
                setSearchBarValue("");
              }}
              noInitialSelection
            />
          </Stack>
        )}
        {historyToUse && historyToUse.transactions.length > 0 ? (
          <div
            style={{ overflow: "auto", maxHeight: "50vh", paddingTop: "3%" }}
            className={classes.scrollBar}
          >
            <LazyLoading
              length={transactions.length}
              items={transactions}
              onRequestPage={(page, cb) => {}}
              pageSize={4}
            >
              <ReactList
                itemRenderer={(idx, key) => (
                  <Grid
                    item
                    md={12}
                    xs={12}
                    key={key}
                    margin={2}
                    onClick={() => handleTransactionClick(transactions[idx])}
                    sx={{ ":hover": { cursor: "pointer" } }}
                  >
                    <SingleLatestTransaction
                      transactionData={transactions[idx]}
                    />
                  </Grid>
                )}
                type="uniform"
                length={transactions.length}
              />
            </LazyLoading>
            {selectedTransaction && (
              <TransactionDetailsDialog
                handleClose={handleClose}
                isConfirmationDialogOpen={true} // Set this to true when you want to open the dialog
                data={selectedTransaction}
              />
            )}
          </div>
        ) : (
          <EmptyTransactionHistory />
        )}
        <div>{userInfo.isLoading && <LinearProgress color="warning" />}</div>
        <Grid container justifyContent="flex-start">
          <Grid item md={12} xs={12}>
            <Fab
              aria-label="add"
              size="small" 
              title={texts["prompt-download-pdf"]}
              onClick={() => {
                if (dateRange[0] == null && dateRange[1] == null) {
                  manageFileStatement("PDF");
                } else {
                  manageFileStatement(
                    "PDF",
                    formatDate(dateRange[0]),
                    formatDate(dateRange[1])
                  );
                }
              }}
              sx={{
                marginTop: 2,
                marginRight: 2,
                marginBottom: 1,
                backgroundColor: colors.lightGray,
                ":hover": { backgroundColor: colors.hoverOrange },
              }}
            >
              <GetAppIcon />
            </Fab>
            <Fab
              aria-label="add"
              size="small" 
              title={texts["prompt-send-email"]}
              onClick={() => {
                if (dateRange[0] == null && dateRange[1] == null) {
                  manageFileStatement("EMAIL");
                } else {
                  manageFileStatement(
                    "EMAIL",
                    formatDate(dateRange[0]),
                    formatDate(dateRange[1])
                  );
                }
              }}
              sx={{
                marginTop: 2,
                marginRight: 2,
                marginBottom: 1,
                backgroundColor: colors.lightGray,
                ":hover": { backgroundColor: colors.hoverOrange },
              }}
            >
              <EmailIcon />
            </Fab>
            <Fab
              aria-label="add"
              size="small" 
              title={texts["prompt-open-graphs"]}
              onClick={handleOpenChartModal}
              sx={{
                marginTop: 2,
                marginBottom: 1,
                backgroundColor: colors.lightGray,
                ":hover": { backgroundColor: colors.hoverOrange },
              }}
            >
              <BarChartIcon />
            </Fab>
            <ChartModal 
            handleClose= {handleCloseChartModal}
            isChartModalOpen = {isChartModalOpen}
            data = {historyToUse}
            fromDate= {dateRange[0] == null ? moment().subtract(1, "months").format("DD/MM/YYYY") :formatDate(dateRange[0])}
            toDate= {dateRange[1] == null ? moment().format("DD/MM/YYYY") : formatDate(dateRange[1])}
            />
          </Grid>
        </Grid>
      </Card>
    </Grid>
  );
};
export default TransactionHistory;
